import { useEffect, useState } from 'preact/hooks';
import type { MeBody } from '../../shared/me';
import type { AppRole } from '../../shared/roles';
import { ApiError, apiGet, prefetch } from './api';
import { startAuth } from './auth';
import { firstLoadPaths } from './prefetch';

export type EntityCode = 'SMC' | 'VIB' | 'MED';

export interface ShellUser {
  name: string | null;
  initials: string;
  entity: EntityCode;
  roles: AppRole[];
  /** Approves a vertical's POs after the CFO (D114). */
  poApprover?: boolean;
}

export const SIGNED_OUT: ShellUser = { name: null, initials: '··', entity: 'SMC', roles: [] };

export type Session =
  | { kind: 'loading' }
  | { kind: 'notConfigured' }
  | { kind: 'signedOut' }
  | { kind: 'signedIn'; me: MeBody; user: ShellUser }
  | { kind: 'error'; code: string; reference: string | null };

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '··';
}

// Roles shown here only shape the menus; the broker re-checks every action. The entity colour
// stays neutral until the broker returns the derived entity (P1.2).
export function toShellUser(me: MeBody): ShellUser {
  return {
    name: me.name || me.email,
    initials: initials(me.name || me.email),
    entity: 'SMC',
    roles: me.roles,
    poApprover: me.poApprover === true,
  };
}

export function useSession(): Session {
  const [session, setSession] = useState<Session>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    const set = (s: Session) => live && setSession(s);
    (async () => {
      try {
        const start = await startAuth();
        if (start.kind !== 'signedIn') return set(start);
        // The page's data travels with the profile instead of after it.
        prefetch(firstLoadPaths(location.hash));
        const me = await apiGet<MeBody>('/api/me');
        set({ kind: 'signedIn', me, user: toShellUser(me) });
      } catch (err) {
        if (err instanceof ApiError)
          set({ kind: 'error', code: err.code, reference: err.correlationId });
        else set({ kind: 'error', code: 'unexpected', reference: null });
      }
    })();
    return () => {
      live = false;
    };
  }, []);
  return session;
}
